import { Paper, List, ListItem, ListItemText, Divider, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from 'shared/functions';
import { ActionsButton, Loader, PanelTitle } from 'ui';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchCoveredBoundsCountries } from 'services';
import { CustomToolbarItem } from 'shared/types/customToolbar';
import { fetchCoveredBoundsDocuments } from 'services/coveredBonds/coveredBondsServices';
import { HomeCoveredBondsDocumentsModal } from 'modules/common/Modals/HomeCoveredBondsDocumentsModal';
import { CountryListItem } from 'modules/creditRating/CoveredBonds/types';
import { prepareCountriesList } from '../../../modules/creditRating/CoveredBonds/functions';

const useStyles = makeStyles(() => ({
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    height: '60px',
  },
  showMoreButton: {
    justifyContent: 'center',
    paddingTop: '12px',
    height: '32px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  countryCount: {
    textAlign: 'right',
  },
  widgetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
  },
}));

export const CoveredBonds: FC<{}> = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const [isShowMore, setIsShowMore] = useState<boolean>(true);
  const [fullCountriesList, setFullCountriesList] = useState<CountryListItem[]>([]);
  const [displayCountriesList, setDisplayCountriesList] = useState<CountryListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [documentsModalOpen, setDocumentsModalOpen] = useState<boolean>(false);
  const [documents, setDocuments] = useState([]);
  const [isDocumentsPending, setIsDocumentsPending] = useState(true);

  const getCountriesData = async () => {
    setLoading(true);
    const countries = await fetchCoveredBoundsCountries();
    const countriesWithNames = prepareCountriesList(countries, 'number', 'DESC');
    setFullCountriesList(countriesWithNames);
    setDisplayCountriesList(countriesWithNames);
    setLoading(false);
  };

  const getCoveredBondsDocuments = () => {
    setDocumentsModalOpen(true);
    setIsDocumentsPending(true);
    fetchCoveredBoundsDocuments()
      .then((response) => setDocuments(response.data))
      .catch((_err) => setDocumentsModalOpen(false))
      .finally(() => setIsDocumentsPending(false));
  };

  useMemo(() => {
    getCountriesData();
  }, []);

  useEffect(() => {
    const halfwayThrough = Math.floor(fullCountriesList.length / 2);
    const result: CountryListItem[] = isShowMore
      ? fullCountriesList.slice(0, halfwayThrough)
      : fullCountriesList;
    setDisplayCountriesList(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowMore, fullCountriesList]);

  const onItemClick = (label: string) => navigate(`/covered-bonds/${label}`);

  const moreActions: CustomToolbarItem[] = [
    {
      text: 'Issuance report',
      handler: () => getCoveredBondsDocuments(),
    },
    {
      text: 'Show all',
      handler: () => onItemClick('all'),
    },
  ];

  return (
    <>
      <Box className={classes.widgetHeader}>
        <PanelTitle>{t('HOME.COVERED_BONDS')}</PanelTitle>
        <ActionsButton actions={moreActions} horizontal variant="text" />
      </Box>
      <Paper>
        <List>
          {loading ? (
            <Loader center />
          ) : (
            displayCountriesList.map((element, index) => (
              <Fragment key={element.countryCode}>
                <ListItem
                  onClick={() => onItemClick(element.countryCode)}
                  button
                  className={classes.listItem}
                >
                  <ListItemText primary={`${element.countryName}`} />
                  <ListItemText
                    primary={`${formatNumber(element.number)}`}
                    className={classes.countryCount}
                  />
                </ListItem>
                {displayCountriesList.length - 1 > index && <Divider />}
              </Fragment>
            ))
          )}
          <Divider />
          <ListItem
            onClick={() => setIsShowMore(!isShowMore)}
            button
            className={classes.showMoreButton}
          >
            {isShowMore ? (
              <>
                {t('SHOW_MORE')} <KeyboardArrowDown />
              </>
            ) : (
              <>
                {t('SHOW_LESS')} <KeyboardArrowUp />
              </>
            )}
          </ListItem>
        </List>
      </Paper>
      <HomeCoveredBondsDocumentsModal
        open={documentsModalOpen}
        isPending={isDocumentsPending}
        onClose={() => setDocumentsModalOpen(!documentsModalOpen)}
        documents={documents}
      />
    </>
  );
};
